/**
 *  @class
 *  @function Predictive Search
 */
if (!customElements.get('search-form')) {
  class SearchForm extends HTMLElement {
    constructor() {
      super();
      this.abortController = null;
      this.requestId = 0;
    }

    connectedCallback() {
      this.form = this.querySelector('form');
      this.input = this.querySelector('input[type="search"]');
      this.predictiveSearchResults = this.querySelector('.thb-predictive-search');
      this.cc = this.querySelector('.searchform--click-capture');
      this.select = this.querySelector('#header-search-select');

      if (!this.form || !this.input || !this.predictiveSearchResults) return;
      this.setupEventListeners();
    }

    disconnectedCallback() {
      if (this.abortController) {
        this.abortController.abort();
      }
    }

    setupEventListeners() {
      this.form.addEventListener('submit', this.onFormSubmit.bind(this));
      this.predictiveSearchResults.addEventListener('focusout', this.close.bind(this));

      this.input.addEventListener('input', debounce(() => {
        this.onChange();
      }, 300));

      if (this.cc) {
        this.cc.addEventListener('click', this.close.bind(this));
        this.cc.addEventListener('wheel', this.close.bind(this), { passive: true });
        this.cc.addEventListener('touchmove', this.close.bind(this), { passive: true });
      }

      this.select?.addEventListener('change', this.close.bind(this));
      window.addEventListener('resize', this.close.bind(this), { passive: true });

      document.addEventListener('keyup', (event) => {
        if (event.code && event.code.toUpperCase() === 'ESCAPE') {
          this.close();
        }
      });
      this.input.addEventListener('focus', this.onFocus.bind(this));
    }

    getQuery() {
      return this.input.value.trim();
    }

    onChange() {
      const searchTerm = this.getQuery();

      if (!searchTerm.length) {
        if (this.abortController) {
          this.abortController.abort();
        }
        this.predictiveSearchResults.classList.remove('loading');
        this.close();
        return;
      }

      this.open();
      this.getSearchResults(searchTerm);
    }

    onFormSubmit(event) {
      if (!this.getQuery().length) {
        event.preventDefault();
      }
    }

    onFocus() {
      const searchTerm = this.getQuery();
      if (!searchTerm.length) return;

      this.open();
      this.getSearchResults(searchTerm);
    }

    async getSearchResults(searchTerm) {
      if (this.abortController) {
        this.abortController.abort();
      }

      const requestId = ++this.requestId;
      this.abortController = new AbortController();
      this.predictiveSearchResults.classList.add('loading');

      const params = new URLSearchParams();
      params.set('q', searchTerm);
      params.set('resources[type]', 'product,collection,query');
      params.set('resources[limit]', '10');
      params.set(
        'resources[options][fields]',
        'title,product_type,variants.title,variants.sku,variants.barcode'
      );
      params.set('section_id', 'predictive-search');

      try {
        const response = await fetch(`${theme.routes.predictive_search_url}?${params.toString()}`, {
          signal: this.abortController.signal,
          headers: { Accept: 'text/html' }
        });

        if (!response.ok) {
          throw new Error(`Predictive search failed with status ${response.status}`);
        }

        const text = await response.text();
        if (requestId !== this.requestId || searchTerm !== this.getQuery()) return;

        const section = new DOMParser()
          .parseFromString(text, 'text/html')
          .querySelector('#shopify-section-predictive-search');

        if (!section) {
          throw new Error('Predictive search response did not contain the expected section');
        }

        this.renderSearchResults(section.innerHTML);
      } catch (error) {
        if (error.name !== 'AbortError') {
          console.error('Error loading predictive search:', error);
        }
      } finally {
        if (requestId === this.requestId) {
          this.predictiveSearchResults.classList.remove('loading');
          this.abortController = null;
        }
      }
    }

    renderSearchResults(resultsMarkup) {
      this.predictiveSearchResults.innerHTML = resultsMarkup;
      this.positionResults();

      const submitButton = this.querySelector('#search-results-submit');
      if (submitButton) {
        submitButton.addEventListener('click', () => {
          this.form.requestSubmit();
        });
      }
    }

    close() {
      this.predictiveSearchResults.setAttribute('inert', '');
      document.body.classList.remove('open-search');
    }

    open() {
      this.positionResults();
      document.body.classList.add('open-search');
      this.predictiveSearchResults.removeAttribute('inert');
    }

    positionResults() {
      const formRect = this.form.getBoundingClientRect();
      const viewportWidth = document.documentElement.clientWidth;
      const isMobile = viewportWidth < 768;
      const viewportPadding = isMobile ? 0 : 24;
      const width = isMobile
        ? viewportWidth
        : Math.min(760, viewportWidth - (viewportPadding * 2));
      const centeredLeft = formRect.left + (formRect.width / 2) - (width / 2);
      const maxLeft = viewportWidth - viewportPadding - width;
      const left = isMobile
        ? 0
        : Math.max(viewportPadding, Math.min(centeredLeft, maxLeft));

      this.predictiveSearchResults.style.setProperty('--predictive-search-top', `${Math.max(0, formRect.bottom + 10)}px`);
      this.predictiveSearchResults.style.setProperty('--predictive-search-left', `${left}px`);
      this.predictiveSearchResults.style.setProperty('--predictive-search-width', `${width}px`);
    }
  }

  customElements.define('search-form', SearchForm);
}
